import { useLocalization } from '@ais-ui/locale-management';
import { useMutateShiplistContext } from './MutateShiplistContext';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  Button,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  InfoIconTooltip,
  Input,
  Label,
  LoadingSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  PiCaretDoubleLeft,
  PiCaretDoubleRight,
  PiCaretLeft,
  PiCaretRight,
  PiFunnel,
  PiPlusThin,
  PiUploadThin,
} from 'react-icons/pi';
import { ShiplistShipRow } from './ShiplistShipRow';
import { UseFieldArrayAppend } from 'react-hook-form';

interface AddShipButtonProps {
  append: UseFieldArrayAppend<
    {
      name: string;
      color: string;
      selectedGroups: { value: string; label: string }[];
      ships: {
        name?: string | undefined;
        mmsi?: number | undefined;
        imo?: string | number | undefined;
      }[];
    },
    'ships'
  >;
  totalShips: number;
  shipsPerPage: number;
  goToPage: (page: number) => void;
}

const AddShipButton = ({
  append,
  totalShips,
  shipsPerPage,
  goToPage,
}: AddShipButtonProps) => {
  const { messages } = useLocalization();
  const { searchQuery, setSearchQuery } = useMutateShiplistContext();

  const handleAddShip = () => {
    append({
      name: '',
      mmsi: undefined,
      imo: undefined,
    });

    // If we're in search mode, clear the search to show all ships including the new one
    if (searchQuery) {
      setSearchQuery('');
    }

    // Navigate to the last page to show the newly added ship
    goToPage(Math.ceil((totalShips + 1) / shipsPerPage));
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id="add-ship-button"
          type="button"
          size="icon"
          variant="outline"
          className="h-9 w-9"
          onClick={handleAddShip}
        >
          <PiPlusThin className="size-4"></PiPlusThin>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{messages.shiplistsSaveAddShipTooltip}</TooltipContent>
    </Tooltip>
  );
};

export const AddShipsToListFormField = () => {
  const { messages } = useLocalization();
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  const { form } = useMutateShiplistContext();

  const {
    totalShips,
    currentShips,
    fields,
    append,
    currentPage,
    itemsPerPage,
    AddGoToPage,
    goToPage,
    totalPages,
    searchQuery,
    setSearchQuery,
    filteredTotalShips,
  } = useMutateShiplistContext();

  const [inputKey, setInputKey] = useState(Date.now());
  const [uploadValidationError, setUploadValidationError] = useState<
    string | null
  >(null);

  const resetShips = () => {
    form.reset({
      ...form.getValues(),
      ships: [],
    });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const onChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setIsLoadingFile(true);
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = async (e: ProgressEvent<FileReader>) => {
        try {
          if (!e.target?.result) {
            return;
          }

          const csvData = e.target.result as string;

          const ships = Papa.parse(csvData, {
            header: true,
            transformHeader: (header: string) => header.toLowerCase(),
          });

          // Validation checks...

          resetShips();

          // Process all ships first
          const processedShips = ships.data
            .filter((ship: any) => ship.name || ship.mmsi || ship.imo)
            .map((ship: any) => ({
              name: ship.name.trim(),
              mmsi: isNaN(parseInt(ship.mmsi, 10))
                ? undefined
                : parseInt(ship.mmsi, 10),
              imo: isNaN(parseInt(ship.imo, 10))
                ? undefined
                : parseInt(ship.imo, 10),
            }));

          // Batch append all ships
          processedShips.forEach((ship, index) => {
            append(ship);
            form.setValue(`ships.${index}`, ship);
          });

          setInputKey(Date.now());
        } finally {
          setIsLoadingFile(false);
        }
      };

      reader.readAsText(file);
    }
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [totalShips]);

  return (
    <FormField
      key="ships"
      control={form.control}
      name="ships"
      render={({ field }) => (
        <FormItem>
          <div className="flex justify-between items-center">
            {/* Group header and search together */}
            <div className="flex space-x-4 items-center flex-1">
              <div className="flex space-x-2 items-center">
                <FormLabel>{messages.shiplistsSaveShipsLabel}</FormLabel>
                <FormDescription>
                  (
                  {searchQuery
                    ? `${filteredTotalShips} of ${fields.length}`
                    : fields.length}
                  )
                </FormDescription>
              </div>
              {uploadValidationError && (
                <Label className="text-destructive">
                  {uploadValidationError}
                </Label>
              )}
              {/* Search Field - grouped with header */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative">
                    <PiFunnel className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={messages.shiplistsSaveFilterShips}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-64 pl-10"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {messages.shiplistsSaveFilterShipsTooltip}
                </TooltipContent>
              </Tooltip>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv, text/csv"
                className="hidden"
                key={inputKey}
                onChange={onFileChange}
              ></Input>
            </div>

            {/* Info icon positioned at far right */}
            <InfoIconTooltip>
              {messages.shiplistsSaveSearchShipsTooltipOuter}
            </InfoIconTooltip>
          </div>

          <FormControl onChange={() => form.clearErrors()}>
            <div className="space-y-4" ref={containerRef}>
              {isLoadingFile && (
                <LoadingSpinner size={'small'}></LoadingSpinner>
              )}
              {!isLoadingFile &&
                currentShips.map((ship, index) => (
                  <ShiplistShipRow
                    key={ship.id}
                    ship={ship}
                    index={ship.originalIndex}
                  />
                ))}
              <div className="flex justify-between">
                <div className="flex space-x-2">
                  <AddShipButton
                    append={append}
                    totalShips={totalShips}
                    shipsPerPage={itemsPerPage}
                    goToPage={AddGoToPage}
                  ></AddShipButton>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="flex space-x-2"
                        onClick={onChooseFileClick}
                      >
                        <PiUploadThin className="size-4"></PiUploadThin>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {messages.shiplistsSaveUploadCsvFile}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex space-x-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="flex space-x-2"
                        onClick={() => goToPage(1)}
                        disabled={currentPage === 1}
                      >
                        <PiCaretDoubleLeft className="size-4"></PiCaretDoubleLeft>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {messages.shiplistsSaveGoToFirstPageTooltip}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="flex space-x-2"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <PiCaretLeft className="size-4"></PiCaretLeft>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {messages.shiplistsSaveGoToPreviousPageTooltip}
                    </TooltipContent>
                  </Tooltip>

                  <Button variant="ghost" disabled={true}>
                    {currentPage}/{totalPages}
                  </Button>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="flex space-x-2"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <PiCaretRight className="size-4"></PiCaretRight>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {messages.shiplistsSaveGoToNextPageTooltip}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="flex space-x-2"
                        onClick={() => goToPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <PiCaretDoubleRight className="size-4"></PiCaretDoubleRight>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {messages.shiplistsSaveGoToLastPageTooltip}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </FormControl>
          {Array.isArray(form?.formState?.errors?.ships) &&
            form.formState.errors.ships.map((error, index) => {
              // If there's an MMSI validation error for this ship, show that first
              if (error?.mmsi?.message) {
                return (
                  <p
                    key={index}
                    className="text-sm font-medium text-destructive"
                  >
                    {`Error in ship ${index + 1}: ${error.mmsi.message}`}
                  </p>
                );
              }
              // Show the "at least one required" error if it exists under root
              else if (error?.root?.message) {
                return (
                  <p
                    key={index}
                    className="text-sm font-medium text-destructive"
                  >
                    {`Error in ship ${index + 1}: ${error.root.message}`}
                  </p>
                );
              }
              return null;
            })}
        </FormItem>
      )}
    />
  );
};
